import { matchPath } from 'react-router-dom';

/**
 * One source for the admin pages' code: the router lazy-loads these loaders,
 * the layout keys its `Suspense` by page (`panelPageKey`), and the panel warms
 * the chunks up before they are needed (`preloadPanelRoute`, `preloadPanelPages`).
 * A navigation always goes through `lazy` (and `lib/chunk-reload` if a chunk is
 * gone after a deploy); the preloads only fill the module cache, so a failed one
 * is swallowed.
 */
export const panelChunks = {
  dashboard: () => import('@/pages/admin/dashboard'),
  notFound: () => import('@/pages/admin/not-found'),
  helpIndex: () => import('@/pages/admin/help'),
  helpArticle: () => import('@/pages/admin/help/article'),
  account: () => import('@/pages/admin/account'),
  newOrganization: () => import('@/pages/admin/organizations/new'),
  orgHome: () => import('@/pages/admin/organizations/dashboard'),
  members: () => import('@/pages/admin/organizations/members'),
  settings: () => import('@/pages/admin/organizations/settings'),
  billing: () => import('@/pages/admin/organizations/billing'),
  stats: () => import('@/pages/admin/organizations/stats'),
  project: () => import('@/pages/admin/organizations/projects/dashboard'),
  projectEdit: () => import('@/pages/admin/organizations/projects/edit'),
  tour: () => import('@/pages/admin/organizations/projects/tours/edit'),
  panoramaEditor: () => import('@/pages/admin/organizations/projects/tours/panorama-editor'),
} satisfies Record<string, () => Promise<{ default: unknown }>>;

export type PanelChunkName = keyof typeof panelChunks;

interface PanelRoute {
  /** React Router path pattern of the page, relative to the app root. */
  pattern: string;
  /** Loader in `panelChunks`; absent when the route renders no page of its own (a redirect). */
  chunk?: PanelChunkName;
  /**
   * Position in the idle warm-up (1 loads first). Absent = not warmed up when
   * idle (rare pages, and the panorama editor, the heaviest chunk).
   */
  idle?: number;
}

const ORG = '/admin/organizations/:orgSlug';
const PROJECT = `${ORG}/projects/:projectSlug`;

/**
 * Most specific first: the first pattern that matches wins. The unknown-path
 * catch-alls (`/admin/*`, `<org>/*`) are NOT here on purpose: each unknown path
 * is its own key and nothing is worth preloading for it.
 */
const ROUTES: PanelRoute[] = [
  { pattern: `${PROJECT}/tours/:tourSlug/panoramas/:panoramaId`, chunk: 'panoramaEditor' },
  { pattern: `${PROJECT}/tours/:tourSlug`, chunk: 'tour', idle: 7 },
  { pattern: `${PROJECT}/edit`, chunk: 'projectEdit' },
  { pattern: PROJECT, chunk: 'project', idle: 2 },
  { pattern: `${ORG}/projects` },
  { pattern: `${ORG}/members`, chunk: 'members', idle: 5 },
  { pattern: `${ORG}/settings`, chunk: 'settings', idle: 3 },
  { pattern: `${ORG}/billing`, chunk: 'billing', idle: 4 },
  { pattern: `${ORG}/stats`, chunk: 'stats', idle: 6 },
  { pattern: '/admin/organizations/new', chunk: 'newOrganization' },
  { pattern: ORG, chunk: 'orgHome', idle: 1 },
  { pattern: '/admin/account', chunk: 'account', idle: 8 },
  { pattern: '/admin/help/:slug', chunk: 'helpArticle' },
  { pattern: '/admin/help', chunk: 'helpIndex' },
  { pattern: '/admin', chunk: 'dashboard' },
];

function matchRoute(pathname: string): PanelRoute | undefined {
  return ROUTES.find(route => matchPath({ path: route.pattern, end: true }, pathname));
}

/**
 * Identity of the page at `pathname`: the route pattern, so moving between two
 * collections (or renaming a slug) keeps the same page instance, exactly like
 * the router does; a path no page claims is its own key.
 */
export function panelPageKey(pathname: string): string {
  return matchRoute(pathname)?.pattern ?? pathname;
}

const started = new Map<PanelChunkName, Promise<unknown>>();

function load(name: PanelChunkName): Promise<unknown> {
  let pending = started.get(name);
  if (!pending) {
    pending = panelChunks[name]().catch(() => {
      // Forget it so a later intent can try again; the real navigation reports the failure.
      started.delete(name);
    });
    started.set(name, pending);
  }
  return pending;
}

/** Downloads the chunk of the page at `pathname` once. Unknown and non-panel paths do nothing. */
export function preloadPanelRoute(pathname: string): void {
  const chunk = matchRoute(pathname)?.chunk;
  if (chunk) void load(chunk);
}

const IDLE_START_MS = 2000;

/** Test hook: forgets what was preloaded. */
export function resetPanelPreloads(): void {
  started.clear();
}

function shouldSkipWarmUp(): boolean {
  if (typeof navigator === 'undefined') return false;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!connection?.saveData || (connection?.effectiveType ?? '').includes('2g');
}

/**
 * After the panel has settled, loads the likely next pages one at a time so a
 * click finds them cached. Skipped on data saver and 2g. Returns a cancel function.
 */
export function preloadPanelPages(): () => void {
  if (shouldSkipWarmUp()) return () => undefined;

  const queue = ROUTES.filter(r => r.chunk && r.idle !== undefined)
    .sort((a, b) => a.idle! - b.idle!)
    .map(r => r.chunk!);

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let idleHandle: number | undefined;

  const whenIdle = (cb: () => void) => {
    if (typeof window.requestIdleCallback === 'function') {
      idleHandle = window.requestIdleCallback(cb, { timeout: 5000 });
    } else {
      timer = setTimeout(cb, 1);
    }
  };

  const next = () => {
    if (cancelled) return;
    const name = queue.shift();
    if (!name) return;
    void load(name).then(() => {
      if (!cancelled) whenIdle(next);
    });
  };

  timer = setTimeout(() => whenIdle(next), IDLE_START_MS);

  return () => {
    cancelled = true;
    if (timer !== undefined) clearTimeout(timer);
    if (idleHandle !== undefined && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(idleHandle);
  };
}
